// Feedback Overview — customer satisfaction KPIs (fNPS, CSI, satisfaction
// rate, ...) aggregated from asset soft points marked kind='feedback'.
// Those points are fed like any other telemetry channel — typically an
// MPulse feedback terminal posting to the device-events webhook (see
// api/src/modules/integrations/device-events.service.js) — except their
// values are a 1-5 satisfaction rating (1 = best) instead of a physical
// measurement. See feedback-analytics.service.js for the scoring formulas.

import { useState } from 'react'
import {
  Alert, Avatar, Box, Card, CardContent, LinearProgress, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { useQuery } from '@tanstack/react-query'
import type { Site } from '@cmms/types'

import ThumbsUpDownOutlinedIcon from '@mui/icons-material/ThumbsUpDownOutlined'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined'
import SentimentDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentDissatisfiedOutlined'
import SentimentNeutralOutlinedIcon from '@mui/icons-material/SentimentNeutralOutlined'
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'

import { api } from '../../lib/api'
import { SearchableSelect } from '../../components/SearchableSelect'
import { LocationTreeSelect } from '../../components/LocationTreeSelect'

// ─── Helpers ────────────────────────────────────────────────────────────────

/** `datetime-local` input value (e.g. "2026-07-21T14:30") for a moment
 *  `minutesAgo` minutes before now, in the browser's own timezone. */
function localInputValue(msAgo: number): string {
  const d = new Date(Date.now() - msAgo)
  d.setSeconds(0, 0)
  const tzOffsetMs = d.getTimezoneOffset() * 60_000
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16)
}

/** A `datetime-local` value is wall-clock time with no timezone info —
 *  `new Date(value)` resolves it against the BROWSER's timezone (correct,
 *  since that's the timezone the user was looking at when they picked
 *  it), and `.toISOString()` then makes it unambiguous for the server,
 *  which may run in a different timezone entirely. */
function toIso(datetimeLocal: string): string {
  return new Date(datetimeLocal).toISOString()
}

function shortDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function fmt(n: number | null, suffix = ''): string {
  return n == null ? '—' : `${n}${suffix}`
}

const RATING_LABELS = ['1 · Excellent', '2 · Good', '3 · Average', '4 · Poor', '5 · Very poor']
const COLOR_PROMOTER = '#10B981'
const COLOR_NEUTRAL  = '#F59E0B'
const COLOR_DETRACTOR = '#EF4444'
const COLOR_CSI  = '#6366F1'
const COLOR_FNPS = '#059669'

// ─── Main component ─────────────────────────────────────────────────────────

export function FeedbackOverview() {
  const [from, setFrom] = useState(() => localInputValue(30 * 86_400_000))
  const [to, setTo]     = useState(() => localInputValue(0))
  const [siteId, setSiteId] = useState('')
  const [locationId, setLocationId] = useState('')

  const sitesQ = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.sites.list(),
    staleTime: 5 * 60_000,
  })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  function onSite(next: string) {
    setSiteId(next)
    setLocationId('')
  }

  const params = {
    from: toIso(from), to: toIso(to),
    site_id: siteId || undefined,
    location_id: locationId || undefined,
  }
  const summaryQ = useQuery({
    queryKey: ['feedback-summary', params.from, params.to, params.site_id, params.location_id],
    queryFn: () => api.feedbackAnalytics.summary(params),
  })
  const trendQ = useQuery({
    queryKey: ['feedback-trend', params.from, params.to, params.site_id, params.location_id],
    queryFn: () => api.feedbackAnalytics.trend(params),
  })

  const summary = summaryQ.data
  const trend = trendQ.data?.points ?? []
  const hasData = (summary?.total_responses ?? 0) > 0

  const presets: Array<{ label: string; msAgo: number }> = [
    { label: '24h', msAgo: 86_400_000 },
    { label: '7d',  msAgo: 7  * 86_400_000 },
    { label: '30d', msAgo: 30 * 86_400_000 },
    { label: '90d', msAgo: 90 * 86_400_000 },
  ]
  const activePreset = presets.find((p) => Math.abs(
    (Date.now() - new Date(from).getTime()) - p.msAgo,
  ) < 5 * 60_000)?.label ?? null

  const distribution = summary?.distribution ?? []
  const promoterData  = distribution.map((d) => d.rating <= 2 ? d.count : null)
  const neutralData   = distribution.map((d) => d.rating === 3 ? d.count : null)
  const detractorData = distribution.map((d) => d.rating >= 4 ? d.count : null)

  return (
    <Stack spacing={3}>
      {/* ─── Filter bar ─── */}
      <Box sx={{
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        border: '1px solid', borderColor: 'divider',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: 'linear-gradient(90deg, #059669 0%, #6366F1 50%, #10B981 100%)',
        },
      }}>
        <Grid container sx={{ p: { xs: 2, md: 2.5 }, pt: { xs: 2.5, md: 3 } }} spacing={{ xs: 2, md: 3 }}>
          <Grid size={{ xs: 12, md: 7 }} sx={{ minWidth: 0 }}>
            <FilterLabel icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />}>
              Date &amp; time
            </FilterLabel>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
              <TextField
                type="datetime-local" size="small"
                value={from} onChange={(e) => setFrom(e.target.value)}
                sx={dateInputSx}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', fontSize: 13, fontWeight: 600, px: 0.25 }}>→</Box>
              <TextField
                type="datetime-local" size="small"
                value={to} onChange={(e) => setTo(e.target.value)}
                sx={dateInputSx}
              />
            </Stack>
            <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.75 }} useFlexGap>
              {presets.map((p) => {
                const active = activePreset === p.label
                return (
                  <Box key={p.label} role="button"
                    onClick={() => { setFrom(localInputValue(p.msAgo)); setTo(localInputValue(0)) }}
                    sx={{
                      px: 1.25, py: 0.4, borderRadius: 99,
                      fontSize: 11, fontWeight: 700, letterSpacing: 0.3, cursor: 'pointer',
                      border: '1px solid',
                      borderColor: active ? 'primary.main' : 'divider',
                      bgcolor: active ? 'primary.main' : 'transparent',
                      color: active ? 'primary.contrastText' : 'text.secondary',
                      transition: 'all 150ms ease', userSelect: 'none',
                      '&:hover': { borderColor: 'primary.main', color: active ? 'primary.contrastText' : 'primary.main' },
                    }}>
                    Last {p.label}
                  </Box>
                )
              })}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }} sx={{ minWidth: 0 }}>
            <FilterLabel icon={<PlaceOutlinedIcon sx={{ fontSize: 14 }} />}>Scope</FilterLabel>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SearchableSelect
                  value={siteId}
                  onChange={onSite}
                  options={[{ id: '', name: 'All sites' } as Site, ...sites]}
                  getValue={(s) => s.id}
                  getLabel={(s) => s.name}
                  placeholder="Site"
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <LocationTreeSelect
                  siteId={siteId || null}
                  value={locationId}
                  onChange={setLocationId}
                  placeholder={siteId ? 'Select location (optional)' : 'Pick a site first'}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {!summaryQ.isLoading && !hasData && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No feedback responses in this range{locationId ? ' for this location' : siteId ? ' for this site' : ''}. Mark an asset&apos;s soft
          point as &quot;Customer feedback&quot; kind (Assets → an asset → Soft points), then link it to an
          MPulse device — see Settings → Integrations — to start collecting ratings.
        </Alert>
      )}

      {/* ─── Hero KPIs ─── */}
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Feedback NPS" value={fmt(summary?.fnps ?? null)} sub="-100 to +100 · loyalty"
            icon={ThumbsUpDownOutlinedIcon} tone="#ECFDF5" accent={COLOR_FNPS} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="CSI" value={fmt(summary?.csi ?? null, '%')} sub="Overall satisfaction"
            icon={SpeedOutlinedIcon} tone="#EEF2FF" accent={COLOR_CSI} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Satisfaction rate" value={fmt(summary?.satisfaction_rate ?? null, '%')} sub="Rated 1-2"
            icon={SentimentSatisfiedAltOutlinedIcon} tone="#ECFDF5" accent="#10B981" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Dissatisfaction rate" value={fmt(summary?.dissatisfaction_rate ?? null, '%')} sub="Rated 4-5"
            icon={SentimentDissatisfiedOutlinedIcon} tone="#FEF2F2" accent="#DC2626" />
        </Grid>
      </Grid>

      {/* ─── Secondary stats ─── */}
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Neutral rate" value={fmt(summary?.neutral_rate ?? null, '%')} sub="Rated 3"
            icon={SentimentNeutralOutlinedIcon} tone="#FFFBEB" accent="#D97706" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Average rating" value={fmt(summary?.average_rating ?? null, ' / 5')} sub="1 = best"
            icon={StarOutlineRoundedIcon} tone="#F3F4F6" accent="#6B7280" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Feedback quality index" value={fmt(summary?.feedback_quality_index ?? null)} sub="0-100 normalized"
            icon={InsightsOutlinedIcon} tone="#EEF2FF" accent="#0D9488" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Total responses" value={String(summary?.total_responses ?? 0)} sub="In selected range"
            icon={ForumOutlinedIcon} tone="#F3F4F6" accent="#6B7280" />
        </Grid>
      </Grid>

      {/* ─── Distribution + trend ─── */}
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
                Feedback distribution
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
                Responses by rating
              </Typography>
              <Box sx={{ height: 260 }}>
                {distribution.length > 0 && hasData ? (
                  <BarChart
                    series={[
                      { data: promoterData,  label: 'Promoters (1-2)', color: COLOR_PROMOTER },
                      { data: neutralData,   label: 'Neutral (3)',     color: COLOR_NEUTRAL },
                      { data: detractorData, label: 'Detractors (4-5)', color: COLOR_DETRACTOR },
                    ]}
                    xAxis={[{ scaleType: 'band', data: RATING_LABELS }]}
                    height={260}
                    borderRadius={4}
                    margin={{ top: 10, right: 8, bottom: 40, left: 32 }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>No data yet</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3.5 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
                fNPS &amp; CSI trend
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
                {trendQ.data?.bucket === 'week' ? 'Weekly' : 'Daily'} over the selected range
              </Typography>
              <Box sx={{ height: 260 }}>
                {trend.length > 1 && hasData ? (
                  <LineChart
                    series={[
                      { data: trend.map((p) => p.fnps), label: 'fNPS', color: COLOR_FNPS, curve: 'monotoneX', showMark: false, connectNulls: true },
                      { data: trend.map((p) => p.csi),  label: 'CSI',  color: COLOR_CSI,  curve: 'monotoneX', showMark: false, connectNulls: true },
                    ]}
                    xAxis={[{ scaleType: 'point', data: trend.map((p) => shortDateTime(p.bucket_start)) }]}
                    yAxis={[{ min: -100, max: 100 }]}
                    height={260}
                    margin={{ top: 10, right: 16, bottom: 40, left: 36 }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Not enough data for a trend yet</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ─── Feedback by location ─── */}
      <Card>
        <CardContent sx={{ p: 3.5 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
            Feedback by location
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
            How many responses came from where, and how satisfied they were
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Location</TableCell>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Site</TableCell>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', width: 140 }}>Responses</TableCell>
                  <TableCell align="right" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Avg rating</TableCell>
                  <TableCell align="right" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>CSI</TableCell>
                  <TableCell align="right" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>fNPS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary?.by_location ?? []).map((row) => {
                  const maxResponses = Math.max(...(summary?.by_location ?? []).map((r) => r.total_responses), 1)
                  return (
                    <TableRow key={row.location_id ?? 'none'}>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{row.location_name}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{row.site_name}</TableCell>
                      <TableCell sx={{ width: 140 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', minWidth: 22 }}>
                            {row.total_responses}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(row.total_responses / maxResponses) * 100}
                            sx={{
                              flex: 1, height: 5, borderRadius: 99,
                              '& .MuiLinearProgress-bar': { bgcolor: COLOR_CSI, borderRadius: 99 },
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{fmt(row.average_rating)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{fmt(row.csi, '%')}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt(row.fnps)}</TableCell>
                    </TableRow>
                  )
                })}
                {(summary?.by_location ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary', fontSize: 13 }}>
                      No feedback responses with a location in this range
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      {/* ─── Counter performance ─── */}
      <Card>
        <CardContent sx={{ p: 3.5 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
            Counter performance
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
            Every feedback point, broken down individually
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Counter</TableCell>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Asset</TableCell>
                  <TableCell align="right" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Responses</TableCell>
                  <TableCell align="right" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Avg rating</TableCell>
                  <TableCell align="right" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>CSI</TableCell>
                  <TableCell align="right" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>fNPS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary?.by_counter ?? []).map((row) => (
                  <TableRow key={row.soft_point_id}>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{row.name}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{row.asset_name}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{row.total_responses}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{fmt(row.average_rating)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{fmt(row.csi, '%')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt(row.fnps)}</TableCell>
                  </TableRow>
                ))}
                {(summary?.by_counter ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary', fontSize: 13 }}>
                      No feedback counters with responses in this range
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  )
}

// ─── Small building blocks ───────────────────────────────────────────────────

function FilterLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
      <Box sx={{ color: 'text.disabled', display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.disabled' }}>
        {children}
      </Typography>
    </Stack>
  )
}

const dateInputSx = {
  flex: 1,
  '& .MuiOutlinedInput-root': { fontFamily: 'ui-monospace, monospace', fontSize: 13 },
  '& input': { py: 1.05 },
} as const

function StatTile({ label, value, sub, icon: Icon, tone, accent }: {
  label: string; value: string; sub?: string; icon: React.ElementType; tone: string; accent: string
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Avatar variant="rounded" sx={{ width: 34, height: 34, bgcolor: tone, color: accent, mb: 1.5 }}>
          <Icon sx={{ fontSize: 18 }} />
        </Avatar>
        <Typography sx={{ color: 'text.secondary', fontSize: 12.5, fontWeight: 500, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.75 }}>{sub}</Typography>
        )}
      </CardContent>
    </Card>
  )
}
